/**
 * Yönetici yetkileri ve hazır roller (K-092). TEK KAYNAK: ekran, API ve seed (03_access) bunu kullanır.
 * Yeni yetki = buraya bir satır + `pnpm db:seed:build`. Kod silinirse DB'deki satır kalır; önce rollerden çıkarın.
 * Super admin bir rol değil, `staff_members.is_super_admin`: her şeye yetkili.
 */

export const MODULES = [
  "cms",
  "marketing",
  "accounting",
  "catalog",
  "site",
  "providers",
  "tasks",
  "support",
  "users",
  "settings",
  "staff",
] as const;
export type Module = (typeof MODULES)[number];

interface PermissionDef {
  description: string;
  /** Para, kişisel veri, güvenilir HTML: role verilirken uyarı (K-092); verilmesi dört göz ister (K-095). */
  sensitive?: true;
}

const defs = {
  // İçerik (P-16, P-17, P-23)
  "cms.pages.edit_own": {
    description: "Kendi sayfa ve yazılarını oluştur/düzenle (yayın onaya gider)",
  },
  "cms.pages.edit_all": { description: "Tüm sayfa ve yazıları düzenle" },
  "cms.pages.publish": { description: "Sayfa ve yazı yayınla/zamanla" },
  "cms.landing_templates.manage": { description: "Landing şablonları" },
  "cms.media.manage": { description: "Medya kütüphanesi" },
  "cms.comments.moderate": { description: "Blog yorumlarını onayla/gizle" },
  "cms.html.trusted": { description: "Güvenilir (script içeren) HTML", sensitive: true },

  // Pazarlama (P-07, P-18, P-20)
  "marketing.campaigns.manage": {
    description: "Reklam kampanyaları, kampanya sayfaları, A/B testleri",
  },
  "marketing.promotions.manage": { description: "Promosyon kodları" },
  "marketing.utm.manage": { description: "UTM ve kısa bağlantılar" },
  "marketing.backlinks.manage": { description: "Backlink takibi" },
  "marketing.messages.manage": {
    description: "E-posta şablonları, segmentler, toplu gönderim, otomasyonlar",
  },
  "marketing.reports.view": { description: "Kampanya raporları (maliyet/getiri)" },

  // Muhasebe (K-089, K-090)
  "accounting.expenses.manage": { description: "Gider ve sabit gider kayıtları" },
  "accounting.budgets.manage": { description: "Bütçeler" },
  "accounting.periods.close": { description: "Muhasebe dönemi kapanışı" },
  "accounting.reconciliation.manage": { description: "Stripe mutabakatı" },
  "accounting.ledger.view": { description: "Ledger kayıtlarını okuma" },
  "accounting.reports.view": { description: "KDV ve gelir-gider raporları" },
  "accounting.exports.run": { description: "Muhasebe dışa aktarma", sensitive: true },

  // Katalog (K-006, K-058, K-093)
  "catalog.categories.manage": { description: "Kategoriler" },
  "catalog.services.manage": { description: "Hizmetler ve pazarlardaki durumu" },
  "catalog.forms.manage": { description: "Form şablonları, sorular, seçenekler" },
  "catalog.fixed_prices.manage": { description: "Sabit fiyat menüleri" },
  "catalog.translations.manage": { description: "Katalog çevirileri" },
  "catalog.ranking.manage": { description: "Önerilen sıralama ağırlıkları" },

  // Site (P-12, P-14)
  "site.menus.manage": { description: "Site menüleri" },
  "site.faqs.manage": { description: "SSS" },
  "site.seo.manage": { description: "SEO sayfaları, yönlendirmeler, 404 ve kırık bağlantılar" },

  // Hizmet verenler (K-043, K-080)
  "providers.applications.review": { description: "Başvuruları incele/onayla" },
  "providers.verification.manage": { description: "Kimlik, belge ve lisans doğrulaması" },
  "providers.quality.manage": { description: "Kalite uyarısı ve askıya alma" },
  "providers.payout_details.change": {
    description: "Ödeme/banka bilgisini değiştir",
    sensitive: true,
  },

  // İşler ve para (K-047, K-050, K-095)
  "tasks.view": { description: "İşleri görüntüle" },
  "tasks.manage": { description: "İşlere müdahale (yeniden ata, iptal)" },
  "tasks.disputes.manage": { description: "İtirazlar" },
  "tasks.urgent.monitor": { description: "Acil çağrı izleme" },
  "tasks.refunds.issue": { description: "İade (eşik üstü dört göz)", sensitive: true },

  // Destek (K-097)
  "support.tickets.manage": { description: "Destek talepleri" },
  "support.reports.moderate": { description: "İçerik bildirimleri" },
  "support.macros.manage": { description: "Hazır cevaplar" },

  // Kullanıcılar (K-100)
  "users.view": { description: "Kullanıcı bilgilerini görüntüle" },
  "users.manage": { description: "Kullanıcı hesabına müdahale (askı, düzeltme)" },
  "users.impersonate": { description: "Kullanıcı gözüyle görüntüleme", sensitive: true },
  "users.data_export": { description: "Kişisel veri dışa aktarma (dört göz)", sensitive: true },

  // Ayarlar
  "settings.markets.manage": { description: "Pazar ayarları" },
  "settings.fees.manage": {
    description: "Komisyon ve ücret kuralları, vergi oranları",
    sensitive: true,
  },
  "settings.flags.manage": { description: "Özellik bayrakları" },
  "settings.integrations.manage": {
    description: "Entegrasyonlar ve API anahtarları",
    sensitive: true,
  },

  // Ekip (K-092, K-095)
  "staff.manage": { description: "Yönetici davet et, rol ver/al", sensitive: true },
  "staff.approvals.decide": { description: "Dört göz isteklerini onayla/reddet" },
  "staff.audit.view": { description: "Denetim kayıtlarını görüntüle" },
} as const satisfies Record<string, PermissionDef>;

export type Permission = keyof typeof defs;

export const PERMISSIONS: readonly {
  code: Permission;
  module: Module;
  description: string;
  sensitive: boolean;
}[] = Object.entries(defs).map(([code, d]) => ({
  code: code as Permission,
  module: code.split(".")[0] as Module,
  description: d.description,
  sensitive: "sensitive" in d && d.sensitive === true,
}));

export const Permission = Object.fromEntries(PERMISSIONS.map((p) => [p.code, p.code])) as {
  readonly [K in Permission]: K;
};

const all = (module: Module) => PERMISSIONS.filter((p) => p.module === module).map((p) => p.code);

/** Hazır roller (K-092 tablosu). Varsayılan yetkiler yalnızca rol ilk oluşturulurken eklenir; sonra admin'den değişir. */
export const SYSTEM_ROLES: readonly {
  code: string;
  name: string;
  description: string;
  permissions: readonly Permission[];
}[] = [
  {
    code: "writer",
    name: "Writer",
    description: "Blog, landing şablonu, serbest sayfa; medya; yorum onayı",
    permissions: [
      "cms.pages.edit_own",
      "cms.pages.edit_all",
      "cms.pages.publish",
      "cms.landing_templates.manage",
      "cms.media.manage",
      "cms.comments.moderate",
    ],
  },
  {
    code: "marketer",
    name: "Marketer",
    description: "Kampanyalar, A/B, UTM, promosyon kodları, backlink, raporlar",
    permissions: all("marketing"),
  },
  {
    code: "accountant",
    name: "Accountant",
    description: "Gider, bütçe, dönem kapanışı, dışa aktarma, raporlar, mutabakat, ledger",
    permissions: all("accounting"),
  },
  {
    code: "manager",
    name: "Manager",
    description: "Katalog, site menüleri, SSS, hizmet veren başvuru/doğrulama, işler",
    permissions: [
      ...all("catalog"),
      ...all("site"),
      "providers.applications.review",
      "providers.verification.manage",
      "tasks.view",
      "tasks.manage",
    ],
  },
  {
    code: "support",
    name: "Support",
    description: "Destek talepleri, işler ve itirazlar, içerik bildirimleri",
    permissions: [...all("support"), "tasks.view", "tasks.disputes.manage", "users.view"],
  },
  {
    code: "operations",
    name: "Operations",
    description: "Hizmet veren onayı, kalite/askı, acil çağrı izleme",
    permissions: [
      "providers.applications.review",
      "providers.verification.manage",
      "providers.quality.manage",
      "tasks.view",
      "tasks.urgent.monitor",
    ],
  },
];
